import { useState } from 'react';
import { Star, Info } from 'lucide-react';
import { ClimateCard } from './ClimateCard';
import { CoverCard } from './CoverCard';
import { GenericEntityCard } from './GenericEntityCard';
import { LightCard } from './LightCard';
import { MediaPlayerCard } from './MediaPlayerCard';
import { SensorCard } from './SensorCard';
import { SwitchCard } from './SwitchCard';
import { useFavorites } from '../../hooks/useFavorites';
import { useHAService } from '../../context/HAConnectionContext';
import { EntityDetailModal } from '../views/EntityDetail';

export interface EntityCardRouterProps {
  entityId: string;
  title?: string;
  cardType?: string;
}

export function domainForEntityId(entityId: string): string {
  return entityId.split('.', 1)[0];
}

function InnerCard({ entityId, title, cardType }: EntityCardRouterProps): JSX.Element {
  const domain = domainForEntityId(entityId);
  const type = cardType ?? domain;
  if (type === 'light' || domain === 'light') return <LightCard entityId={entityId} title={title} />;
  if (type === 'switch' || domain === 'switch') return <SwitchCard entityId={entityId} title={title} />;
  if (type === 'sensor' || domain === 'sensor' || domain === 'binary_sensor') return <SensorCard entityId={entityId} title={title} />;
  if (type === 'cover' || domain === 'cover') return <CoverCard entityId={entityId} title={title} />;
  if (type === 'climate' || domain === 'climate') return <ClimateCard entityId={entityId} title={title} />;
  if (type === 'media-player' || type === 'media_player' || domain === 'media_player') return <MediaPlayerCard entityId={entityId} title={title} />;
  return <GenericEntityCard entityId={entityId} title={title} />;
}

// Domains, deren PRIMAER-Klick eine Aktion ausloest (Tor auf, Szene an, ...).
// Detail-Modal ist dann per Info-Icon (oben rechts) erreichbar.
const ACTION_DOMAINS = new Set(['button', 'scene', 'script', 'input_button']);

function actionForDomain(domain: string): { service: string; data?: object } | null {
  if (domain === 'button' || domain === 'input_button') return { service: 'press' };
  if (domain === 'scene') return { service: 'turn_on' };
  if (domain === 'script') return { service: 'turn_on' };
  return null;
}

export function EntityCard(props: EntityCardRouterProps): JSX.Element {
  const { toggle, has } = useFavorites();
  const { callService } = useHAService();
  const isFav = has(props.entityId);
  const [detailOpen, setDetailOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [flash, setFlash] = useState<'ok' | 'err' | null>(null);

  const domain = domainForEntityId(props.entityId);
  const isAction = ACTION_DOMAINS.has(domain);
  const action = actionForDomain(domain);

  const triggerAction = async () => {
    if (!action || pending) return;
    setPending(true);
    try {
      await callService(domain, action.service, action.data, { entity_id: props.entityId });
      setFlash('ok');
    } catch (err) {
      console.error('[entity-card] action failed', props.entityId, err);
      setFlash('err');
    } finally {
      setPending(false);
      setTimeout(() => setFlash(null), 900);
    }
  };

  const handleCardClick: React.MouseEventHandler = (e) => {
    // Nur oeffnen wenn Klick nicht auf einen interaktiven Kind-Button (Toggle etc.) ging.
    const target = e.target as HTMLElement;
    const el = target.closest('button, [role="slider"], input, a');
    if (el && el !== e.currentTarget) return;
    if (isAction) {
      void triggerAction();
    } else {
      setDetailOpen(true);
    }
  };

  const flashRing =
    flash === 'ok'
      ? 'ring-2 ring-emerald-400'
      : flash === 'err'
      ? 'ring-2 ring-red-400'
      : pending
      ? 'ring-2 ring-amber-400 animate-pulse'
      : '';

  return (
    <>
      <div
        className={`relative cursor-pointer rounded-2xl transition-shadow ${flashRing}`}
        onClick={handleCardClick}
        data-testid={`entity-card-${props.entityId}`}
        role={isAction ? 'button' : undefined}
        aria-busy={pending}
      >
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); toggle(props.entityId); }}
          aria-label={isFav ? 'Von Favoriten entfernen' : 'Zu Favoriten hinzufuegen'}
          aria-pressed={isFav}
          data-testid={`fav-toggle-${props.entityId}`}
          className={`absolute top-1 left-1 z-10 flex h-6 w-6 items-center justify-center rounded-full transition-all ${
            isFav
              ? 'bg-amber-400/20 text-amber-400 opacity-100'
              : 'text-gray-400 opacity-40 hover:opacity-100 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          <Star className={`h-3.5 w-3.5 ${isFav ? 'fill-current' : ''}`} aria-hidden="true" />
        </button>
        {isAction && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setDetailOpen(true); }}
            aria-label="Details anzeigen"
            data-testid={`info-${props.entityId}`}
            className="absolute top-1 right-1 z-10 flex h-6 w-6 items-center justify-center rounded-full text-gray-400 opacity-40 hover:opacity-100 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <Info className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        )}
        <InnerCard {...props} />
      </div>
      <EntityDetailModal entityId={detailOpen ? props.entityId : null} onOpenChange={(open) => setDetailOpen(open)} />
    </>
  );
}
